<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h3 class="text-h5">Registro de Implantes</h3>
          <v-btn color="primary" @click="showCreateDialog = true" prepend-icon="mdi-plus">
            Registrar Implante
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Filtros y Búsqueda -->
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="busquedaPaciente"
          label="Buscar por RUN del Paciente"
          prepend-inner-icon="mdi-magnify"
          clearable
          @update:model-value="fetchImplantes"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          v-model="filtroEstado"
          :items="estadosImplante"
          label="Estado del Implante"
          clearable
          @update:model-value="fetchImplantes"
        ></v-select>
      </v-col>
    </v-row>

    <!-- Tabla de Implantes -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="implantes"
          :loading="loading"
          class="elevation-1"
        >
          <template v-slot:item.fecha_implantacion="{ item }">
            {{ formatDateTime(item.fecha_implantacion) }}
          </template>

          <template v-slot:item.estado="{ item }">
            <v-chip :color="getEstadoColor(item)" size="small">
              {{ item.fecha_explantacion ? 'Explantado' : 'Activo' }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              v-if="!item.fecha_explantacion"
              icon="mdi-minus-circle"
              size="small"
              color="warning"
              @click="registrarExplante(item)"
              title="Registrar Explante"
            ></v-btn>
            <!-- Comentado temporalmente - no se usará por el momento
            <v-btn
              icon="mdi-eye"
              size="small"
              @click="verDetalle(item)"
              title="Ver Detalle"
            ></v-btn>
            -->
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Diálogo: Registrar Implante -->
    <v-dialog v-model="showCreateDialog" max-width="900" persistent>
      <v-card>
        <v-card-title>Registrar Implante en Paciente</v-card-title>
        <v-card-text>
          <v-form ref="createForm">
            <v-row>
              <!-- Datos del Paciente -->
              <v-col cols="12">
                <v-alert type="info" density="compact" variant="tonal">
                  <strong>Datos del Paciente</strong>
                </v-alert>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="nuevoImplante.run_paciente"
                  label="RUN del Paciente *"
                  :rules="[v => !!v || 'Requerido', v => validarRun(v) || 'RUN inválido']"
                  required
                  hint="Formato: 12345678-9"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="nuevoImplante.nombre_paciente"
                  label="Nombre del Paciente *"
                  :rules="[v => !!v || 'Requerido']"
                  required
                ></v-text-field>
              </v-col>

              <!-- Datos del Dispositivo -->
              <v-col cols="12">
                <v-alert type="info" density="compact" variant="tonal">
                  <strong>Dispositivo Médico</strong>
                </v-alert>
              </v-col>

              <v-col cols="12">
                <v-autocomplete
                  v-model="nuevoImplante.inventory_item_id"
                  :items="dispositivosImplantables"
                  item-title="descripcion"
                  item-value="id"
                  label="Dispositivo Médico Implantable *"
                  :rules="[v => !!v || 'Requerido']"
                  required
                >
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <v-list-item-subtitle>
                        Lote: {{ item.raw.lote }} | S/N: {{ item.raw.serial_number || 'N/A' }} | Categoría: {{ item.raw.categoria }}
                      </v-list-item-subtitle>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- Datos del Procedimiento -->
              <v-col cols="12">
                <v-alert type="info" density="compact" variant="tonal">
                  <strong>Datos del Procedimiento</strong>
                </v-alert>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="nuevoImplante.fecha_implantacion"
                  label="Fecha y Hora de Implantación *"
                  type="datetime-local"
                  :rules="[v => !!v || 'Requerido']"
                  required
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="nuevoImplante.nombre_profesional_implante"
                  label="Profesional que Implanta *"
                  :rules="[v => !!v || 'Requerido']"
                  required
                  hint="Nombre completo del médico cirujano"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="nuevoImplante.prestacion_quirurgica"
                  label="Prestación Quirúrgica *"
                  :rules="[v => !!v || 'Requerido']"
                  required
                  hint="Nombre del procedimiento"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="nuevoImplante.codigo_prestacion"
                  label="Código de Prestación"
                  hint="Código FONASA o GES si aplica"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="nuevoImplante.observaciones"
                  label="Observaciones"
                  rows="3"
                  hint="Detalles adicionales del procedimiento"
                  persistent-hint
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="cancelCreate">Cancelar</v-btn>
          <v-btn 
            color="primary" 
            @click="crearImplante" 
            :loading="submitting"
            :disabled="!isImplanteFormValid"
          >
            Registrar Implante
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Registrar Explante -->
    <v-dialog v-model="showExplanteDialog" max-width="600" persistent>
      <v-card>
        <v-card-title>Registrar Explante</v-card-title>
        <v-card-text>
          <v-form ref="explanteForm">
            <v-alert type="warning" class="mb-4">
              <strong>Atención:</strong> Va a registrar el retiro del dispositivo implantado.
            </v-alert>

            <v-text-field
              v-model="datosExplante.fecha_explantacion"
              label="Fecha y Hora de Explantación *"
              type="datetime-local"
              :rules="[v => !!v || 'Requerido']"
              required
            ></v-text-field>

            <v-text-field
              v-model="datosExplante.nombre_profesional_explante"
              label="Profesional que Explanta *"
              :rules="[v => !!v || 'Requerido']"
              required
            ></v-text-field>

            <v-select
              v-model="datosExplante.motivo_explante"
              :items="motivosExplante"
              label="Motivo del Explante *"
              :rules="[v => !!v || 'Requerido']"
              required
            ></v-select>

            <v-textarea
              v-model="datosExplante.observaciones"
              label="Observaciones *"
              rows="3"
              :rules="[v => !!v || 'Requerido']"
              required
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="cancelarExplante">Cancelar</v-btn>
          <v-btn 
            color="warning" 
            @click="confirmarExplante" 
            :loading="submitting"
            :disabled="!isExplanteFormValid"
          >
            Confirmar Explante
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import apiClient from '@/api'

export default {
  name: 'ImplanteDM',
  data() {
    return {
      loading: false,
      submitting: false,
      showCreateDialog: false,
      showExplanteDialog: false,
      busquedaPaciente: '',
      filtroEstado: null,
      implantes: [],
      dispositivosImplantables: [],
      selectedImplante: null,
      nuevoImplante: {
        inventory_item_id: null,
        run_paciente: '',
        nombre_paciente: '',
        fecha_implantacion: '',
        nombre_profesional_implante: '',
        prestacion_quirurgica: '',
        codigo_prestacion: '',
        observaciones: ''
      },
      datosExplante: {
        fecha_explantacion: '',
        nombre_profesional_explante: '',
        motivo_explante: '',
        observaciones: ''
      },
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },
      headers: [
        { title: 'ID', key: 'id', sortable: true },
        { title: 'RUN Paciente', key: 'run_paciente', sortable: true },
        { title: 'Paciente', key: 'nombre_paciente', sortable: true },
        { title: 'Dispositivo', key: 'dispositivo_nombre', sortable: true },
        { title: 'Fecha Implantación', key: 'fecha_implantacion', sortable: true },
        { title: 'Profesional', key: 'nombre_profesional_implante', sortable: true },
        { title: 'Estado', key: 'estado', sortable: false },
        { title: 'Acciones', key: 'actions', sortable: false }
      ],
      estadosImplante: [
        { title: 'Activo', value: 'activo' },
        { title: 'Explantado', value: 'explantado' }
      ],
      motivosExplante: [
        'Complicación',
        'Reemplazo',
        'Fin de vida útil',
        'Decisión del paciente',
        'Otro'
      ]
    }
  },
  computed: {
    isImplanteFormValid() {
      return !!(
        this.nuevoImplante.run_paciente &&
        this.nuevoImplante.nombre_paciente &&
        this.nuevoImplante.inventory_item_id &&
        this.nuevoImplante.fecha_implantacion &&
        this.nuevoImplante.nombre_profesional_implante &&
        this.nuevoImplante.prestacion_quirurgica &&
        this.validarRun(this.nuevoImplante.run_paciente)
      )
    },
    isExplanteFormValid() {
      return !!(
        this.datosExplante.fecha_explantacion &&
        this.datosExplante.nombre_profesional_explante &&
        this.datosExplante.motivo_explante &&
        this.datosExplante.observaciones
      )
    }
  },
  watch: {
    showCreateDialog(newVal) {
      if (newVal) {
        // Limpiar formulario cuando se abre el diálogo
        this.resetNuevoImplante()
      }
    },
    showExplanteDialog(newVal) {
      if (newVal && this.selectedImplante) {
        // Limpiar formulario cuando se abre el diálogo de explante
        this.resetDatosExplante()
      }
    }
  },
  mounted() {
    this.fetchImplantes()
    this.fetchDispositivosImplantables()
  },
  methods: {
    async fetchImplantes() {
      this.loading = true
      try {
        const params = {}
        if (this.busquedaPaciente) params.run_paciente = this.busquedaPaciente
        if (this.filtroEstado) params.estado = this.filtroEstado

        const response = await apiClient.get('/dm/implantes', { params })
        this.implantes = response.data || []
      } catch (error) {
        console.error('Error al cargar implantes:', error)
        this.showSnackbar('Error al cargar implantes', 'error')
      } finally {
        this.loading = false
      }
    },

    async fetchDispositivosImplantables() {
      try {
        const response = await apiClient.get('/dm/dispositivos-dm', {
          params: { tipo: 'Implantable' }
        })
        this.dispositivosImplantables = response.data.map(item => ({
          id: item.id,
          descripcion: `${item.product_name} - ${item.sku}`,
          lote: item.lot_number,
          serial_number: item.serial_number,
          categoria: item.categorizacion_implantable
        }))
      } catch (error) {
        console.error('Error al cargar dispositivos:', error)
      }
    },

    async crearImplante() {
      if (!this.$refs.createForm.validate()) return

      this.submitting = true
      try {
        await apiClient.post('/dm/implantes', this.nuevoImplante)
        
        this.showSnackbar('Implante registrado exitosamente', 'success')
        this.showCreateDialog = false
        // Limpiar formulario después de registrar exitosamente
        this.resetNuevoImplante()
        this.fetchImplantes()
        this.$emit('refresh')
      } catch (error) {
        console.error('Error al registrar implante:', error)
        this.showSnackbar('Error al registrar implante', 'error')
      } finally {
        this.submitting = false
      }
    },

    resetDatosExplante() {
      this.datosExplante = {
        fecha_explantacion: '',
        nombre_profesional_explante: '',
        motivo_explante: '',
        observaciones: ''
      }
      // Resetear validación del formulario
      if (this.$refs.explanteForm) {
        this.$refs.explanteForm.resetValidation()
      }
    },

    registrarExplante(implante) {
      this.selectedImplante = implante
      this.resetDatosExplante()
      this.showExplanteDialog = true
    },

    async confirmarExplante() {
      if (!this.$refs.explanteForm.validate()) return

      this.submitting = true
      try {
        await apiClient.put(`/dm/implantes/${this.selectedImplante.id}/explante`, this.datosExplante)
        
        this.showSnackbar('Explante registrado exitosamente', 'success')
        this.showExplanteDialog = false
        // Limpiar formulario después de registrar exitosamente
        this.resetDatosExplante()
        this.selectedImplante = null
        this.fetchImplantes()
        this.$emit('refresh')
      } catch (error) {
        console.error('Error al registrar explante:', error)
        this.showSnackbar('Error al registrar explante', 'error')
      } finally {
        this.submitting = false
      }
    },

    verDetalle(implante) {
      // TODO: Implementar vista de detalle completo
      console.log('Ver detalle de implante:', implante)
    },

    resetNuevoImplante() {
      this.nuevoImplante = {
        inventory_item_id: null,
        run_paciente: '',
        nombre_paciente: '',
        fecha_implantacion: '',
        nombre_profesional_implante: '',
        prestacion_quirurgica: '',
        codigo_prestacion: '',
        observaciones: ''
      }
      // Resetear validación del formulario
      if (this.$refs.createForm) {
        this.$refs.createForm.resetValidation()
      }
    },

    cancelCreate() {
      this.showCreateDialog = false
      this.resetNuevoImplante()
    },

    cancelarExplante() {
      this.showExplanteDialog = false
      this.resetDatosExplante()
      this.selectedImplante = null
    },

    validarRun(run) {
      // Validación básica de RUN chileno
      if (!run) return false
      const cleanRun = run.replace(/[.-]/g, '')
      if (cleanRun.length < 8 || cleanRun.length > 9) return false
      return /^\d{7,8}[0-9Kk]$/.test(cleanRun)
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleString('es-ES')
    },

    getEstadoColor(implante) {
      return implante.fecha_explantacion ? 'grey' : 'success'
    },

    showSnackbar(message, color = 'success') {
      this.snackbar = { show: true, message, color }
    }
  }
}
</script>

